import { apiGet } from '@/services/apiClient'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  updateDoc,
  doc,
  deleteDoc,
} from '@/services/neonFirestore'
import { db } from '@/firebase/config'
import { useAuthStore } from './auth'
import { useCompanyStore } from './company'
import { submitPunch, resumePendingPunch } from '@/services/attendanceDelivery'
import { logStaffActivity } from '@/services/staffActivityLog'
import { filterBySubCompanyScope, applySubCompanyMetadata } from '@/utils/subCompanyScope'
import { assignedShiftForDate } from '@/utils/roster/assignedShift'

const MINUTES_IN_DAY = 24 * 60
const DEFAULT_EARLY_CLOCK_IN_WINDOW_MINUTES = 15
/** Minimum time between any clock-in/out punches (anti-spam). */
export const CLOCK_ACTION_COOLDOWN_MS = 30_000

/** Coerce Firestore Timestamp / ISO / Date / epoch into a Date without throwing. */
const toDateSafe = (value) => {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value?.toDate === 'function') {
    try {
      const d = value.toDate()
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null
    } catch {
      return null
    }
  }
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6)
  }
  if (typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

const mapAttendanceDoc = (docSnap) => {
  const data = docSnap.data() || {}
  const timestamp = toDateSafe(data.timestamp)
  if (!timestamp) return null

  return {
    id: docSnap.id,
    ...data,
    timestamp,
    createdAt: toDateSafe(data.createdAt) || timestamp,
    updatedAt: toDateSafe(data.updatedAt) || timestamp,
    adjustmentTimestamp: toDateSafe(data.adjustmentTimestamp),
  }
}

const parseTimeToMinutes = (time) => {
  if (!time) return 0
  const [hours, minutes] = String(time || '00:00').split(':').map(Number)
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }

  return hours * 60 + minutes
}

const formatMinutesAsTime = (totalMinutes) => {
  const normalizedMinutes = ((totalMinutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY
  const hours = Math.floor(normalizedMinutes / 60)
  const minutes = normalizedMinutes % 60

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const getEarlyClockInWindowMinutes = (schedule) => {
  const rawMinutes = schedule?.earlyClockInWindowMinutes ?? schedule?.gracePeriod
  const minutes = rawMinutes === undefined || rawMinutes === null
    ? DEFAULT_EARLY_CLOCK_IN_WINDOW_MINUTES
    : Number(rawMinutes)

  if (!Number.isFinite(minutes) || minutes < 0) return 0
  return Math.min(minutes, MINUTES_IN_DAY - 1)
}

const subtractMinutesFromTime = (time, minutes) => {
  const timeMinutes = parseTimeToMinutes(time)
  if (timeMinutes === null) return time

  return formatMinutesAsTime(timeMinutes - minutes)
}

const normalizeShiftIds = (userProfile = {}) => {
  const rawShiftIds = Array.isArray(userProfile.shiftIds)
    ? userProfile.shiftIds
    : (userProfile.shiftId ? [userProfile.shiftId] : [])

  return [...new Set(rawShiftIds
    .map((shiftId) => (typeof shiftId === 'string' ? shiftId.trim() : ''))
    .filter(Boolean))]
}

export const isTimeWithinClockInWindow = (
  currentTime,
  startTime,
  endTime,
  earlyClockInWindowMinutes = 0,
) => {
  const currentMinutes = parseTimeToMinutes(currentTime)
  const startMinutes = parseTimeToMinutes(startTime)
  const endMinutes = parseTimeToMinutes(endTime)

  if (currentMinutes === null || startMinutes === null || endMinutes === null) return false

  const adjustedStartMinutes = (
    startMinutes - Math.max(0, earlyClockInWindowMinutes) + MINUTES_IN_DAY
  ) % MINUTES_IN_DAY

  if (adjustedStartMinutes <= endMinutes) {
    return currentMinutes >= adjustedStartMinutes && currentMinutes <= endMinutes
  }

  return currentMinutes >= adjustedStartMinutes || currentMinutes <= endMinutes
}

export const useAttendanceStore = defineStore('attendance', () => {
  const attendanceRecords = ref([])
  const geofences = ref([])
  const loading = ref(false)
  const clockActionInFlight = ref(false)
  const deliveryMessage = ref('')
  const activeRosterWindow = ref(null)
  /** Client-side stamp used when DB reload lags behind a successful punch. */
  const lastSuccessfulPunchAt = ref(null)
  const currentLocation = ref(null)
  const currentSchedule = ref(null)
  const lastReminderDismissed = ref(null)
  const lastClockInReminderDismissed = ref(null)
  const systemConfigs = ref({
    attendance: {
      faceRecognitionEnabled: true
    }
  })

  const getRecordTimestampMs = (record) => {
    const ts = new Date(record?.timestamp).getTime()
    return Number.isFinite(ts) ? ts : null
  }

  /**
   * Latest punch that should drive live clock status.
   * Future-dated manual/adjusted entries are ignored so they cannot hide a real
   * clock-in and keep showing the Clock In button.
   */
  const getLatestAttendanceRecord = ({ includeFuture = false } = {}) => {
    if (!attendanceRecords.value.length) return null

    // Allow small device/server clock skew
    const nowMs = Date.now() + 2 * 60 * 1000
    const eligible = attendanceRecords.value.filter((record) => {
      const ts = getRecordTimestampMs(record)
      if (ts == null) return false
      return includeFuture || ts <= nowMs
    })

    if (!eligible.length) return null

    return [...eligible].sort(
      (a, b) => getRecordTimestampMs(b) - getRecordTimestampMs(a),
    )[0]
  }

  const getClockActionCooldownRemaining = () => {
    const candidates = []
    const nowMs = Date.now()
    if (lastSuccessfulPunchAt.value && lastSuccessfulPunchAt.value <= nowMs) {
      candidates.push(lastSuccessfulPunchAt.value)
    }
    const latest = getLatestAttendanceRecord()
    if (latest?.timestamp) {
      const ts = getRecordTimestampMs(latest)
      // Ignore future timestamps — they would create an endless cooldown
      if (ts != null && ts <= nowMs) candidates.push(ts)
    }
    if (!candidates.length) return 0
    const lastPunchMs = Math.max(...candidates)
    const remaining = CLOCK_ACTION_COOLDOWN_MS - (nowMs - lastPunchMs)
    return remaining > 0 ? remaining : 0
  }

  const isClockActionOnCooldown = computed(() => getClockActionCooldownRemaining() > 0)

  const authStore = useAuthStore()
  const companyStore = useCompanyStore()
  const getBusinessOwnerSubCompanyId = () => {
    const role = (authStore.userProfile?.role || '').toString().toLowerCase()
    if (role !== 'business_owner') return null
    return authStore.userProfile?.subCompanyId || null
  }

  const createOpenAttendanceSchedule = (overrides = {}) => ({
    branchId: authStore.userProfile?.branchId || null,
    source: 'open_attendance',
    type: 'work',
    startTime: '00:00',
    endTime: '23:59',
    gracePeriod: 0,
    earlyClockInWindowMinutes: 0,
    isFlexible: true,
    isOpenAttendance: true,
    ...overrides,
  })

  const getEmployeeScopedSubCompanyId = (employeeId) => {
    if (!employeeId) return authStore.userProfile?.subCompanyId || null
    const employee = companyStore.companyEmployees.find((emp) => emp.uid === employeeId)
    return employee?.subCompanyId || authStore.userProfile?.subCompanyId || null
  }

  // Get today's attendance records for current user
  const todaysRecords = computed(() => {
    if (!authStore.user) return []

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    return attendanceRecords.value.filter((record) => {
      const recordDate = new Date(record.timestamp)
      return recordDate >= today && recordDate < tomorrow
    })
  })

  // Check if user is currently clocked in (persists across days until manual clock out)
  const isClockedIn = computed(() => {
    const lastRecord = getLatestAttendanceRecord()
    return lastRecord?.type === 'clock-in'
  })

  /** Active open clock-in record (overnight-aware), or null when clocked out. */
  const activeClockInRecord = computed(() => {
    if (!isClockedIn.value) return null
    const latest = getLatestAttendanceRecord()
    return latest?.type === 'clock-in' ? latest : null
  })

  // Get current geofence for user's location
  const currentGeofence = computed(() => {
    if (!currentLocation.value) return null

    const userLat = currentLocation.value.coords.latitude
    const userLng = currentLocation.value.coords.longitude

    return geofences.value.find((geofence) => {
      if (!geofence.isActive) return false

      const distance = calculateDistance(
        userLat,
        userLng,
        geofence.center.latitude,
        geofence.center.longitude,
      )

      return distance <= geofence.radius
    })
  })

  // Check if face recognition is globally enabled by superadmin
  const isFaceRecognitionGloballyEnabled = computed(() => {
    return systemConfigs.value.attendance?.faceRecognitionEnabled !== false
  })

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180
    const φ2 = (lat2 * Math.PI) / 180
    const Δφ = ((lat2 - lat1) * Math.PI) / 180
    const Δλ = ((lon2 - lon1) * Math.PI) / 180

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c
  }

  // Get current location (with retry using lower accuracy if high-accuracy times out)
  const getCurrentLocation = (refreshSchedule = true) => {
    const requestPosition = (options) =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by this browser'))
          return
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, options)
      })

    const storeAndMaybeRefresh = async (position) => {
      currentLocation.value = {
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        },
        timestamp: position.timestamp,
      }

      if (refreshSchedule) {
        try {
          await loadCurrentSchedule(currentLocation.value)
        } catch (err) {
          console.warn('Schedule refresh failed after location update:', err)
        }
      }
      return position
    }

    return requestPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    })
      .then(storeAndMaybeRefresh)
      .catch(async (highAccuracyError) => {
        console.warn('High-accuracy location failed, retrying:', highAccuracyError?.message)
        try {
          const position = await requestPosition({
            enableHighAccuracy: false,
            timeout: 20000,
            maximumAge: 60000,
          })
          return storeAndMaybeRefresh(position)
        } catch (fallbackError) {
          const code = fallbackError?.code
          if (code === 1) {
            throw new Error('Location permission denied. Enable GPS/location for this site in browser settings.')
          }
          if (code === 2) {
            throw new Error('Location unavailable. Move outdoors or nearer a window, then try again.')
          }
          if (code === 3) {
            throw new Error('Location request timed out. Check GPS is on and try refresh again.')
          }
          throw fallbackError
        }
      })
  }

  const readAssignedShift = async (shiftId) => {
    if (!shiftId) return null
    try {
      const shiftDoc = await getDoc(doc(db, 'shifts', shiftId))
      if (!shiftDoc.exists()) return null
      return { id: shiftDoc.id, ...shiftDoc.data() }
    } catch (error) {
      console.warn('Unable to load assigned shift:', shiftId, error)
      return null
    }
  }

  // Load current schedule (Roster -> Assigned Shifts -> Default Profile)
  const loadCurrentSchedule = async (position = null) => {
    if (!authStore.user || !authStore.userProfile) return null

    try {
      const now = new Date()
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      const currentTime = now.toTimeString().slice(0, 5)
      
      const allPossibleSchedules = []

      // 1. Get roster entries for today
      const rosterQuery = query(
        collection(db, 'roster_entries'),
        where('companyId', '==', authStore.userProfile.companyId),
        where('employeeId', '==', authStore.user.uid),
        where('date', '==', todayStr)
      )
      const rosterSnapshot = await getDocs(rosterQuery)
      let overtime = { requests: [], rosters: [] }
      try {
        const result = await apiGet('/v1/attendance-actions/overtime')
        if (Array.isArray(result.requests)) overtime = result
      } catch (error) {
        console.warn('Unable to refresh approved OT:', error.message)
      }
      const approvedFor = (id, data) => overtime.requests.find(request =>
        request.rosterId === id && request.status === 'approved'
        && Date.parse(request.originalStart) === Date.parse(`${data.date || todayStr}T${data.startTime}:00+08:00`)
        && new Date(request.originalEnd).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kuala_Lumpur', hour: '2-digit', minute: '2-digit' }) === data.endTime)
      const rosterDocs = [...rosterSnapshot.docs]
      // Include the previous day's active overnight roster/OT without confusing it with today's slot.
      for (const slot of overtime.rosters || []) {
        const request = approvedFor(slot.id, slot)
        const end = request ? Date.parse(request.requestedEnd) : slot.end
        if (!rosterDocs.some(item => item.id === slot.id) && slot.start <= Date.now() && end >= Date.now()) {
          rosterDocs.push({ id: slot.id, data: () => slot })
        }
      }
      
      const rosterSchedules = await Promise.all(rosterDocs.map(async (rosterDoc) => {
        const data = rosterDoc.data()
        const approvedOt = approvedFor(rosterDoc.id, data)
        let shiftData = null

        if (data.shiftId) {
          shiftData = await readAssignedShift(data.shiftId)
        }

        return {
          id: rosterDoc.id,
          startTime: data.startTime,
          endTime: approvedOt
            ? new Date(approvedOt.requestedEnd).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kuala_Lumpur', hour: '2-digit', minute: '2-digit' })
            : data.endTime,
          originalEndTime: data.endTime,
          approvedOtMinutes: approvedOt?.minutes || 0,
          startsAt: approvedOt ? Date.parse(approvedOt.originalStart) : data.start,
          effectiveEndAt: approvedOt ? Date.parse(approvedOt.requestedEnd) : data.end,
          branchId: data.branchId || shiftData?.branchId || authStore.userProfile.branchId,
          shiftId: data.shiftId,
          source: 'roster',
          type: data.type,
          isRoster: true,
          isFlexible: data.isFlexible || shiftData?.isFlexible || false,
          gracePeriod: data.gracePeriod ?? shiftData?.gracePeriod,
          earlyClockInWindowMinutes: data.earlyClockInWindowMinutes ?? shiftData?.earlyClockInWindowMinutes,
        }
      }))

      allPossibleSchedules.push(...rosterSchedules)

      // 2. Get assigned shifts for today
      const shiftIds = normalizeShiftIds(authStore.userProfile)
      for (const sId of shiftIds) {
        const shiftData = await readAssignedShift(sId)
        if (!shiftData || !assignedShiftForDate({ shiftId: sId }, now, [shiftData])) continue
        allPossibleSchedules.push({
          id: sId,
          startTime: shiftData.isFlexible ? '00:00' : shiftData.startTime,
          endTime: shiftData.isFlexible ? '23:59' : shiftData.endTime,
          branchId: shiftData.branchId || authStore.userProfile.branchId,
          shiftId: sId,
          source: shiftData.isFlexible ? `flexible shift: ${shiftData.name}` : `assigned shift: ${shiftData.name}`,
          type: 'work',
          gracePeriod: shiftData.gracePeriod,
          earlyClockInWindowMinutes: shiftData.earlyClockInWindowMinutes,
          isFlexible: shiftData.isFlexible,
          isShift: true
        })
      }

      if (allPossibleSchedules.length === 0) {
        // No roster or assigned shift: allow attendance at any time.
        const defaultSchedule = createOpenAttendanceSchedule()
        currentSchedule.value = defaultSchedule
        return defaultSchedule
      }

      // 3. Selection Logic: prioritize location if available, otherwise time match
      let selectedSchedule = null
      const loc = position || currentLocation.value
      const userLat = loc?.coords?.latitude
      const userLng = loc?.coords?.longitude
      const accuracy = loc?.coords?.accuracy || 0
      const matchesCurrentTime = (schedule) => {
        if (schedule.isFlexible) return true
        if (schedule.startsAt && schedule.effectiveEndAt) {
          return Date.now() >= schedule.startsAt - getEarlyClockInWindowMinutes(schedule) * 60000
            && Date.now() <= schedule.effectiveEndAt
        }
        return isTimeWithinClockInWindow(
          currentTime,
          schedule.startTime,
          schedule.endTime,
          getEarlyClockInWindowMinutes(schedule),
        )
      }

      if (userLat && userLng) {
        // Try to find a schedule where user is within the branch radius
        for (const s of allPossibleSchedules) {
          if (s.type !== 'work' || !s.branchId) continue
          
          const isAtLocation = companyStore.isWithinLocationRadius(userLat, userLng, s.branchId, accuracy)
          if (isAtLocation) {
            // Found a location match!
            const scheduleMatchesNow = matchesCurrentTime(s)
            const selectedMatchesNow = selectedSchedule ? matchesCurrentTime(selectedSchedule) : false
            if (!selectedSchedule || 
                (scheduleMatchesNow && !selectedMatchesNow)) {
              selectedSchedule = s
            }
          }
        }

        // NEW: If user is Company-wide (no fixed branch) and no schedule matched yet,
        // check if they are currently at ANY company branch
        if (!selectedSchedule && !authStore.userProfile.branchId) {
          const nearbyBranch = companyStore.findNearbyBranch?.(userLat, userLng, accuracy)
          if (nearbyBranch) {
            // If they are at a branch, and we have multiple schedules, pick the best time match
            // or create a dynamic one from the first work schedule but with this branch
            const firstWorkSchedule = allPossibleSchedules.find(s => s.type === 'work')
            if (firstWorkSchedule) {
              selectedSchedule = { ...firstWorkSchedule, branchId: nearbyBranch.id }
            } else if (allPossibleSchedules.length === 0) {
              // No schedules at all, create a default one for this branch
              selectedSchedule = createOpenAttendanceSchedule({
                branchId: nearbyBranch.id,
                source: 'nearby_branch',
              })
            }
          }
        }
      }

      // 4. Default Selection Logic: pick best time match or just the first one
      if (!selectedSchedule) {
        // Filter to work schedules first
        const workSchedules = allPossibleSchedules.filter(s => s.type === 'work')
        if (workSchedules.length > 0) {
           // Sort by time match
           const timeSorted = [...workSchedules].sort((a, b) => {
              const aInTime = matchesCurrentTime(a)
              const bInTime = matchesCurrentTime(b)
              if (aInTime && !bInTime) return -1
              if (!aInTime && bInTime) return 1
              return 0
           })
           selectedSchedule = timeSorted[0]
        } else if (allPossibleSchedules.length > 0) {
          // If all are non-work (leave, etc.), pick the first one which will block clock-in
          selectedSchedule = allPossibleSchedules[0]
        } else {
          // Absolute fallback
          selectedSchedule = createOpenAttendanceSchedule()
        }
      }

      // Final check for Company-wide employees: if they are at a branch but the schedule has no branchId, assign it
      if (userLat && userLng && !selectedSchedule.branchId && !authStore.userProfile.branchId) {
        const nearbyBranch = companyStore.findNearbyBranch?.(userLat, userLng, accuracy)
        if (nearbyBranch) {
          selectedSchedule.branchId = nearbyBranch.id
        }
      }

      currentSchedule.value = selectedSchedule
      return selectedSchedule
    } catch (error) {
      console.error('Error loading current schedule:', error)
      return null
    }
  }

  // Clock in/out with video capture
  const clockInOut = async (type, faceMatchConfidence, videoBlob = null, meta = {}) => {
    if (!authStore.user || !authStore.userProfile) {
      throw new Error('User not authenticated')
    }

    if (type !== 'clock-in' && type !== 'clock-out') {
      throw new Error('Invalid attendance type')
    }

    if (clockActionInFlight.value) {
      throw new Error('Attendance action already in progress. Please wait.')
    }

    const activitySource = meta?.source || 'modern'
    clockActionInFlight.value = true

    try {
      // Always refresh from DB so multi-tab / lag cannot allow duplicate punches
      await loadTodaysAttendance({ silent: true })

      const latest = getLatestAttendanceRecord()
      const cooldownRemaining = getClockActionCooldownRemaining()
      if (cooldownRemaining > 0) {
        const waitSec = Math.ceil(cooldownRemaining / 1000)
        throw new Error(
          `Please wait ${waitSec}s before clocking ${type === 'clock-in' ? 'in' : 'out'} again.`,
        )
      }

      if (type === 'clock-in' && latest?.type === 'clock-in') {
        throw new Error('You are already clocked in. Please clock out first.')
      }
      if (type === 'clock-out' && latest?.type !== 'clock-in') {
        throw new Error('You are not clocked in. Please clock in first.')
      }

      const position = currentLocation.value && Date.now() - currentLocation.value.timestamp < 60000
        ? currentLocation.value
        : await getCurrentLocation(false)
      
      // Load/Validate current schedule - pass position to ensure we use latest coords
      let schedule = await loadCurrentSchedule(position)
      if (!schedule) {
        console.warn('Unable to determine current schedule, using open attendance fallback.')
        schedule = createOpenAttendanceSchedule()
        currentSchedule.value = schedule
      }

      let effectiveBranchId = schedule.branchId || authStore.userProfile.branchId || null
      
      // FORCED FALLBACK: If still no branchId and user is Company-wide, try one last geolocation check
      if (!effectiveBranchId && !authStore.userProfile.branchId) {
          const nearbyBranch = companyStore.findNearbyBranch?.(
            position.coords.latitude, 
            position.coords.longitude, 
            position.coords.accuracy
          )
          if (nearbyBranch) {
            effectiveBranchId = nearbyBranch.id
          }
      }
      let activeShiftId = schedule.shiftId || null

      // Validate working hours for clock-in (Tally with Roster or Assigned Shift)
      if (type === 'clock-in') {
        const now = new Date()
        const currentTime = now.toTimeString().slice(0, 5) // HH:MM format

        // Allow clock-in even when roster marks leave/off.
        // Time window enforcement only applies to work schedules.
        if (schedule.type === 'work') {
          const startTime = schedule.startTime
          const endTime = schedule.endTime
          const scheduleSource = schedule.source
          const earlyWindowMinutes = getEarlyClockInWindowMinutes(schedule)

          // 4. Validate time against the found schedule (Skip for flexible)
          let isWithinHours = false
          if (schedule.isFlexible) {
            isWithinHours = true
          } else if (schedule.startsAt && schedule.effectiveEndAt) {
            isWithinHours = Date.now() >= schedule.startsAt - earlyWindowMinutes * 60000
              && Date.now() <= schedule.effectiveEndAt
          } else {
            isWithinHours = isTimeWithinClockInWindow(
              currentTime,
              startTime,
              endTime,
              earlyWindowMinutes,
            )
          }

          if (!isWithinHours) {
            const allowedStartTime = subtractMinutesFromTime(startTime, earlyWindowMinutes)
            const allowedMessage = earlyWindowMinutes > 0
              ? `from ${allowedStartTime} to ${endTime} for your ${scheduleSource} hours (${startTime} - ${endTime})`
              : `during your ${scheduleSource} hours (${startTime} - ${endTime})`
            throw new Error(`Clock-in is only allowed ${allowedMessage}.`)
          }
        }
      }

      // Validate minimum work hours for flexible shifts (clock-out)
      if (type === 'clock-out') {
        const shiftIds = normalizeShiftIds(authStore.userProfile)
        if (shiftIds.length > 0) {
          // Check all assigned shifts to see if any are flexible for today
          const now = new Date()

          for (const sId of shiftIds) {
            let shiftDoc
            try {
              shiftDoc = await getDoc(doc(db, 'shifts', sId))
            } catch (shiftError) {
              console.warn('Unable to validate flexible shift during clock-out; continuing with clock-out.', {
                shiftId: sId,
                code: shiftError?.code,
                message: shiftError?.message,
              })
              continue
            }

            if (shiftDoc.exists()) {
              const shiftData = { id: shiftDoc.id, ...shiftDoc.data() }
              if (shiftData.isFlexible && assignedShiftForDate({ shiftId: sId }, now, [shiftData])) {
                 // Find the most recent clock-in
                 const sortedRecords = [...attendanceRecords.value].sort(
                   (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                 )
                 const latestClockIn = sortedRecords.find(r => r.type === 'clock-in')
                 
                 if (latestClockIn) {
                   const clockInTime = new Date(latestClockIn.timestamp)
                   const nowTime = new Date()
                   const diffMs = nowTime - clockInTime
                   const diffHours = diffMs / (1000 * 60 * 60)
                   
                   const requiredHours = shiftData.requiredWorkHours || 8
                   if (diffHours < requiredHours) {
                     const remainingHours = (requiredHours - diffHours).toFixed(1)
                     throw new Error(`Flexible shift (${shiftData.name}) requires minimum ${requiredHours} hours of work. You have worked for ${diffHours.toFixed(1)} hours. Please clock out in ${remainingHours} hours.`)
                   }
                   break // Found applicable flexible shift, checked it, can stop
                 }
              }
            }
          }
        }
      }

      // Validate location if geolocation is enabled (check branch first, then company)
      const geolocationSettings = companyStore.getGeolocationSettings(effectiveBranchId)

      if (geolocationSettings?.enabled) {
        const locationResult = companyStore.resolveAttendanceBranch(
          position.coords.latitude,
          position.coords.longitude,
          effectiveBranchId,
          position.coords.accuracy,
        )

        if (!locationResult.isValid) {
          throw new Error(
            `You must be within ${locationResult.radius || geolocationSettings.radius}m of ${locationResult.branchName} to clock ${type === 'clock-in' ? 'in' : 'out'}`,
          )
        }

        if (locationResult.branchId) {
          effectiveBranchId = locationResult.branchId
        }
      } else if (companyStore.currentCompany?.settings?.geolocation?.enabled) {
        // Fallback check for company-level geolocation
        const isWithinRadius = companyStore.isWithinLocationRadius(
          position.coords.latitude,
          position.coords.longitude,
          null,
          position.coords.accuracy
        )

        if (!isWithinRadius) {
          const radius = companyStore.currentCompany.settings.geolocation.radius
          throw new Error(
            `You must be within ${radius}m of the workplace to clock ${type === 'clock-in' ? 'in' : 'out'}`,
          )
        }
      }

      // Check if user is within a geofence (legacy support)
      const geofence = currentGeofence.value
      if (!geofence && !companyStore.currentCompany?.settings?.geolocation?.enabled) {
        // No location settings enabled - allowing clock in/out
      }

      // Commit attendance before uploading optional video evidence.
      const finalVideoUrl = null
      const uploadErrorDetails = null
      // Build the attendance event; attach video separately after server acknowledgement.
      const attendanceRecord = applySubCompanyMetadata({
        userId: authStore.user.uid,
        companyId: authStore.userProfile.companyId,
        type,
        timestamp: new Date(),
        location: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          address: '', // TODO: Implement reverse geocoding
        },
        geofenceId: geofence ? geofence.id : null,
        branchId: effectiveBranchId || null,
        shiftId: activeShiftId || null,
        faceMatchConfidence: faceMatchConfidence || 0,
        videoUrl: finalVideoUrl, // Set to actual URL if uploaded, null otherwise
        isAdjusted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      }, authStore.userProfile)

      console.log('Creating attendance record with videoUrl:', finalVideoUrl)

      const savedRecord = await submitPunch(authStore.user.uid, {
        ...attendanceRecord,
        timestamp: attendanceRecord.timestamp.toISOString(),
        rosterId: schedule.source === 'roster' ? schedule.id : null,
        clockInRecordId: type === 'clock-out' ? latest?.id : null,
        hasVideo: Boolean(videoBlob),
      }, videoBlob)
      const docRef = { id: savedRecord.id }
      attendanceRecord.timestamp = new Date(savedRecord.timestamp)
      console.log('Attendance record created with ID:', docRef.id)

      // Optimistically update local state so UI flips to Clocked In/Out even if reload fails
      const localRecord = {
        id: docRef.id,
        ...attendanceRecord,
      }
      attendanceRecords.value = [localRecord, ...attendanceRecords.value.filter((r) => r.id !== docRef.id)]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      lastSuccessfulPunchAt.value = Date.now()

      // Auto-create roster entry if clocking in and none exists
      if (type === 'clock-in') {
        try {
          // Use local date string instead of UTC to avoid issues with timezones
          const now = new Date()
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

          // Check for existing roster entry
          const rosterQuery = query(
            collection(db, 'roster_entries'),
            where('companyId', '==', authStore.userProfile.companyId),
            where('employeeId', '==', authStore.user.uid),
            where('date', '==', todayStr)
          )
          const rosterSnapshot = await getDocs(rosterQuery)

          if (rosterSnapshot.empty) {
            console.log('No roster found for today, auto-creating...')
            const assignedShiftIds = normalizeShiftIds(authStore.userProfile)
            const fallbackRosterSchedule = schedule || createOpenAttendanceSchedule()
            let startTime = fallbackRosterSchedule.startTime || '00:00'
            let endTime = fallbackRosterSchedule.endTime || '23:59'
            let shiftId = fallbackRosterSchedule.shiftId || null
            let isFlexible = !!fallbackRosterSchedule.isFlexible

            if (!fallbackRosterSchedule.isOpenAttendance && !shiftId && assignedShiftIds.length > 0) {
              const shiftDoc = await getDoc(doc(db, 'shifts', assignedShiftIds[0]))
              if (shiftDoc.exists()) {
                const shiftData = shiftDoc.data()
                startTime = shiftData.isFlexible ? '00:00' : (shiftData.startTime || startTime)
                endTime = shiftData.isFlexible ? '23:59' : (shiftData.endTime || endTime)
                shiftId = shiftDoc.id
                isFlexible = !!shiftData.isFlexible
              }
            }

            const rosterEntry = applySubCompanyMetadata({
              companyId: authStore.userProfile.companyId,
              employeeId: authStore.user.uid,
              date: todayStr,
              type: 'work',
              startTime,
              endTime,
              shiftId,
              isFlexible,
              branchId: effectiveBranchId || authStore.userProfile.branchId || null,
              createdAt: Timestamp.fromDate(new Date()),
              updatedAt: Timestamp.fromDate(new Date())
            }, authStore.userProfile)

            if (fallbackRosterSchedule.isOpenAttendance) {
              rosterEntry.source = 'open_attendance'
            }

            await addDoc(collection(db, 'roster_entries'), rosterEntry)
            console.log('Auto-created roster entry for today')
          }
        } catch (rosterError) {
          console.error('Error auto-creating roster entry:', rosterError)
          // Don't fail the clock-in if roster creation fails
        }
      }

      // Best-effort refresh from DB (local optimistic state already updated)
      await loadTodaysAttendance({ silent: true })
      // Keep the just-written punch if DB reload is eventually consistent / lagging
      if (!attendanceRecords.value.some((r) => r.id === docRef.id)) {
        attendanceRecords.value = [localRecord, ...attendanceRecords.value]
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      }

      void logStaffActivity({
        authStore,
        action: type,
        outcome: 'success',
        source: activitySource,
        attendanceId: docRef.id,
        branchId: effectiveBranchId || null,
      })

      return {
        id: docRef.id,
        videoUrl: finalVideoUrl || null,
        uploadError: uploadErrorDetails || null,
      }
    } catch (error) {
      void logStaffActivity({
        authStore,
        action: type,
        outcome: 'failed',
        source: activitySource,
        reason: error?.message || 'Clock action failed',
        branchId: authStore.userProfile?.branchId || null,
      })

      deliveryMessage.value = error.message?.includes('pending delivery') ? error.message : ''
      console.error('❌ Clock in/out error:', error)
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack,
      })

      // Provide more helpful error messages
      if (
        error.code === 'storage/retry-limit-exceeded' ||
        error.code === 'unavailable' ||
        error.message?.includes('network') ||
        error.message?.includes('ERR_NAME_NOT_RESOLVED') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('offline')
      ) {
        throw new Error(
          'Network error: No internet connection. Please connect to the internet and try again.',
        )
      } else if (error.code === 'storage/unauthorized') {
        throw new Error(
          'Video upload failed: Permission denied. Please check storage security rules.',
        )
      } else if (error.message?.includes('video')) {
        throw new Error(`Video upload failed: ${error.message}`)
      }

      throw error
    } finally {
      clockActionInFlight.value = false
    }
  }

  // Load today's attendance records and the most recent status
  const loadTodaysAttendance = async ({ silent = false } = {}) => {
    if (!authStore.user) return

    if (!silent) loading.value = true
    try {
      try {
        await resumePendingPunch(authStore.user.uid)
        deliveryMessage.value = ''
      } catch (error) {
        deliveryMessage.value = error.message
      }

      let serverState = null
      try {
        serverState = await apiGet('/v1/attendance-actions/state')
        activeRosterWindow.value = serverState.window
      } catch (error) {
        console.warn('Unable to refresh roster deadline:', error.message)
      }
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      // Query 1: Today's records
      const qToday = query(
        collection(db, 'attendance'),
        where('userId', '==', authStore.user.uid),
        where('timestamp', '>=', Timestamp.fromDate(today)),
        where('timestamp', '<', Timestamp.fromDate(tomorrow)),
        orderBy('timestamp', 'desc'),
      )

      // Query 2: Most recent records (handles overnight open sessions)
      const qLatest = query(
        collection(db, 'attendance'),
        where('userId', '==', authStore.user.uid),
        orderBy('timestamp', 'desc'),
        limit(10),
      )

      // Prefer partial success over total failure — overnight status depends on qLatest
      const [todayResult, latestResult] = await Promise.allSettled([
        getDocs(qToday),
        getDocs(qLatest),
      ])

      const recordsMap = new Map()
      if (serverState?.latest) {
        const record = serverState.latest
        recordsMap.set(record.id, { ...record, timestamp: new Date(record.timestamp) })
      }


      const processDoc = (docSnap) => {
        const mapped = mapAttendanceDoc(docSnap)
        if (mapped) {
          recordsMap.set(mapped.id, mapped)
        } else {
          console.warn('Skipping attendance record with invalid timestamp:', docSnap.id)
        }
      }

      if (todayResult.status === 'fulfilled') {
        todayResult.value.docs.forEach(processDoc)
      } else {
        console.error('Failed loading today attendance:', todayResult.reason)
      }

      if (latestResult.status === 'fulfilled') {
        latestResult.value.docs.forEach(processDoc)
      } else {
        console.error('Failed loading latest attendance status:', latestResult.reason)
      }

      if (todayResult.status === 'rejected' && latestResult.status === 'rejected') {
        throw todayResult.reason || latestResult.reason
      }

      attendanceRecords.value = Array.from(recordsMap.values()).sort(
        (a, b) => b.timestamp - a.timestamp,
      )
    } catch (error) {
      console.error('Error loading attendance records:', error)
    } finally {
      if (!silent) loading.value = false
    }
  }

  // Load all attendance records for current user
  const loadAttendanceHistory = async () => {
    if (!authStore.user) return

    loading.value = true
    try {
      const q = query(
        collection(db, 'attendance'),
        where('userId', '==', authStore.user.uid),
        orderBy('timestamp', 'desc')
      )

      const querySnapshot = await getDocs(q)
      attendanceRecords.value = querySnapshot.docs
        .map((docSnap) => mapAttendanceDoc(docSnap))
        .filter(Boolean)
    } catch (error) {
      console.error('Error loading attendance history:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Load all attendance records for company (manager only) - today only
  const loadCompanyAttendanceRecords = async () => {
    if (!authStore.userProfile?.companyId) return

    loading.value = true
    try {
      // Get today's attendance records for the company (query by companyId directly)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()

      const attendanceQuery = businessOwnerSubCompanyId
        ? query(
            collection(db, 'attendance'),
            where('companyId', '==', authStore.userProfile.companyId),
            where('subCompanyId', '==', businessOwnerSubCompanyId),
            where('timestamp', '>=', Timestamp.fromDate(today)),
            where('timestamp', '<', Timestamp.fromDate(tomorrow)),
          )
        : query(
            collection(db, 'attendance'),
            where('companyId', '==', authStore.userProfile.companyId),
            where('timestamp', '>=', Timestamp.fromDate(today)),
            where('timestamp', '<', Timestamp.fromDate(tomorrow)),
          )

      const querySnapshot = await getDocs(attendanceQuery)
      const loadedRecords = querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          adjustmentTimestamp: data.adjustmentTimestamp?.toDate(),
        }
      })
      attendanceRecords.value = filterBySubCompanyScope(loadedRecords, authStore.userProfile)
    } catch (error) {
      console.error('Error loading company attendance records:', error)
    } finally {
      loading.value = false
    }
  }

  // Load all attendance records for company (manager only) - all time
  const loadAllCompanyAttendanceRecords = async () => {
    if (!authStore.userProfile?.companyId) return

    loading.value = true
    try {
      // Query directly by companyId (much more efficient than userId 'in' queries)
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      const attendanceQuery = businessOwnerSubCompanyId
        ? query(
            collection(db, 'attendance'),
            where('companyId', '==', authStore.userProfile.companyId),
            where('subCompanyId', '==', businessOwnerSubCompanyId),
          )
        : query(
            collection(db, 'attendance'),
            where('companyId', '==', authStore.userProfile.companyId),
          )

      const querySnapshot = await getDocs(attendanceQuery)
      const loadedRecords = querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          adjustmentTimestamp: data.adjustmentTimestamp?.toDate(),
        }
      })
      attendanceRecords.value = filterBySubCompanyScope(loadedRecords, authStore.userProfile)
    } catch (error) {
      console.error('Error loading all company attendance records:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Load geofences for company
  const loadGeofences = async () => {
    if (authStore.authLoading) return

    if (!authStore.userProfile?.companyId && !authStore.isSuperAdmin) {
      console.warn('Cannot load geofences: No companyId in user profile')
      return
    }

    loading.value = true
    try {
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      let q
      if (authStore.isSuperAdmin && !authStore.userProfile?.companyId) {
        q = query(collection(db, 'geofences'), where('isActive', '==', true))
      } else if (businessOwnerSubCompanyId) {
        q = query(
          collection(db, 'geofences'),
          where('companyId', '==', authStore.userProfile.companyId),
          where('subCompanyId', '==', businessOwnerSubCompanyId),
          where('isActive', '==', true),
        )
      } else {
        q = query(
          collection(db, 'geofences'),
          where('companyId', '==', authStore.userProfile.companyId),
          where('isActive', '==', true),
        )
      }

      const querySnapshot = await getDocs(q)
      const loadedGeofences = querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        }
      })
      geofences.value = filterBySubCompanyScope(loadedGeofences, authStore.userProfile)
    } catch (error) {
      console.error('Error loading geofences:', error)
      if (error.code === 'permission-denied') {
        console.error('Permission denied loading geofences. User:', {
          uid: authStore.userProfile?.uid,
          role: authStore.userProfile?.role,
          companyId: authStore.userProfile?.companyId
        })
      }
    } finally {
      loading.value = false
    }
  }

  // Create new geofence (manager only)
  const createGeofence = async (geofenceData) => {
    if (!authStore.userProfile || !authStore.isManager) {
      throw new Error('Unauthorized')
    }

    try {
      const geofence = {
        ...geofenceData,
        companyId: authStore.userProfile.companyId,
        createdAt: new Date(),
        updatedAt: new Date(),
      }
      const scopedGeofence = applySubCompanyMetadata(geofence, authStore.userProfile)

      const docRef = await addDoc(collection(db, 'geofences'), {
        ...scopedGeofence,
        createdAt: Timestamp.fromDate(scopedGeofence.createdAt),
        updatedAt: Timestamp.fromDate(scopedGeofence.updatedAt),
      })

      await loadGeofences()
      return docRef.id
    } catch (error) {
      throw error
    }
  }

  // Check if user needs a clock-in reminder (15 minutes after shift start)
  const checkClockInReminder = async () => {
    if (!authStore.user) return false

    // If already clocked in, no reminder needed
    if (isClockedIn.value) return false

    // Ensure we have today's records loaded
    if (attendanceRecords.value.length === 0) {
      await loadTodaysAttendance()
    }

    // Double check after loading
    if (isClockedIn.value) return false

    const todayStr = new Date().toDateString()
    if (lastClockInReminderDismissed.value === todayStr) return false

    try {
      let startTime = '09:00'

      // Try to get shift start time
      if (authStore.userProfile?.shiftId) {
        const shiftDoc = await getDoc(doc(db, 'shifts', authStore.userProfile.shiftId))
        if (shiftDoc.exists()) {
          startTime = shiftDoc.data().startTime
        }
      } else if (companyStore.currentCompany?.settings?.workHours) {
        startTime = companyStore.currentCompany.settings.workHours.start || '09:00'
      }

      const now = new Date()
      const [hours, minutes] = String(startTime || '09:00').split(':').map(Number)

      const shiftStart = new Date()
      shiftStart.setHours(hours, minutes, 0, 0)

      // We remind if they are late (i.e., past start time)
      const lateThreshold = shiftStart

      // Only remind if within 4 hours of start
      const maxThreshold = new Date(shiftStart.getTime() + 4 * 60 * 60 * 1000)

      return now >= lateThreshold && now <= maxThreshold
    } catch (error) {
      console.error('Error in checkClockInReminder:', error)
      return false
    }
  }

  const dismissClockInReminder = () => {
    lastClockInReminderDismissed.value = new Date().toDateString()
  }

  // Check if user needs a clock-out reminder (30 minutes after shift end)
  const checkClockOutReminder = async () => {
    if (!authStore.user) return false

    // If not clocked in, no reminder needed
    if (!isClockedIn.value) return false

    const todayStr = new Date().toDateString()
    if (lastReminderDismissed.value === todayStr) return false

    try {
      const state = await apiGet('/v1/attendance-actions/state')
      if (state.latest?.type !== 'clock-in') return false
      if (state.window) {
        activeRosterWindow.value = state.window
        return Date.now() >= state.window.effectiveEnd - 10 * 60000 && Date.now() < state.window.effectiveEnd
      }

      let endTime = '18:00'

      // Try to get shift end time
      if (authStore.userProfile?.shiftId) {
        const shiftDoc = await getDoc(doc(db, 'shifts', authStore.userProfile.shiftId))
        if (shiftDoc.exists()) {
          endTime = shiftDoc.data().endTime
        }
      } else if (companyStore.currentCompany?.settings?.workHours) {
        endTime = companyStore.currentCompany.settings.workHours.end || '18:00'
      }

      const now = new Date()
      const [hours, minutes] = String(endTime || '18:00').split(':').map(Number)

      const shiftEnd = new Date()
      shiftEnd.setHours(hours, minutes, 0, 0)

      // Remind 10 minutes before shift suppose clock out
      const reminderThreshold = new Date(shiftEnd.getTime() - 10 * 60 * 1000)

      // Only remind within the window (from 10 mins before to the actual end time)
      return now >= reminderThreshold && now < shiftEnd
    } catch (error) {
      console.error('Error in checkClockOutReminder:', error)
      return false
    }
  }

  const dismissReminder = () => {
    lastReminderDismissed.value = new Date().toDateString()
  }

  // Detect attendance issues: clock-in, clock-out, location, and working hours
  const detectAttendanceIssues = async () => {
    if (!authStore.user || !authStore.userProfile) {
      return {
        hasIssues: false,
        notClockedIn: false,
        notClockedOut: false,
        outOfLocation: false,
        outOfWorkingHours: false,
        issues: [],
        messages: {},
      }
    }

    const issues = []
    const messages = {}
    let hasIssues = false

    // Check if user didn't clock in today (and isn't currently clocked in from a previous day)
    const clockInRecordToday = todaysRecords.value.find((record) => record.type === 'clock-in')
    const notClockedIn = !clockInRecordToday && !isClockedIn.value

    if (notClockedIn) {
      hasIssues = true
      issues.push('notClockedIn')
      messages.notClockedIn = 'You have not clocked in today'
    }

    // Check if user didn't clock out (clocked in but no clock-out after)
    let notClockedOut = false
    if (isClockedIn.value) {
      // Find the most recent clock-in (which is active since isClockedIn is true)
      const latestClockIn = [...attendanceRecords.value]
        .sort((a, b) => b.timestamp - a.timestamp)
        .find(r => r.type === 'clock-in');

      if (latestClockIn) {
        // If it's an old clock-in (e.g., > 24 hours ago), it might be a forgotten clock-out
        const hoursOpen = (new Date() - latestClockIn.timestamp) / (1000 * 60 * 60);
        if (hoursOpen > 20) { // arbitrary threshold for forgotten clock-out
          notClockedOut = true;
          hasIssues = true;
          issues.push('notClockedOut');
          messages.notClockedOut = 'You have an active session from a previous day. Did you forget to clock out?';
        }
      }
    }

    // Check if user is out of location area
    let outOfLocation = false
    try {
      // Get current location if not already available
      if (!currentLocation.value) {
        await getCurrentLocation()
      }

      if (currentLocation.value) {
        const userLat = currentLocation.value.coords.latitude
        const userLng = currentLocation.value.coords.longitude
        const userBranchId = authStore.userProfile?.branchId

        // Check branch geolocation first, then company geolocation
        const geolocationSettings = companyStore.getGeolocationSettings(userBranchId)
        const geolocationEnabled = geolocationSettings?.enabled

        if (geolocationEnabled) {
          const isWithinRadius = companyStore.isWithinLocationRadius(userLat, userLng, userBranchId)
          outOfLocation = !isWithinRadius

          if (outOfLocation) {
            hasIssues = true
            issues.push('outOfLocation')
            const locationName = companyStore.currentBranch?.name || 'workplace'
            messages.outOfLocation = `You are outside the work area (must be within ${geolocationSettings.radius}m of ${locationName})`
          }
        } else {
          // Check company-level geolocation as fallback
          const companyGeolocationEnabled =
            companyStore.currentCompany?.settings?.geolocation?.enabled
          if (companyGeolocationEnabled) {
            const isWithinRadius = companyStore.isWithinLocationRadius(userLat, userLng)
            outOfLocation = !isWithinRadius

            if (outOfLocation) {
              hasIssues = true
              issues.push('outOfLocation')
              const radius = companyStore.currentCompany.settings.geolocation.radius
              messages.outOfLocation = `You are outside the work area (must be within ${radius}m of the workplace)`
            }
          } else {
            // Check legacy geofence system
            if (!currentGeofence.value && companyStore.currentCompany?.settings?.geolocation) {
              outOfLocation = true
              hasIssues = true
              issues.push('outOfLocation')
              messages.outOfLocation = 'You are outside a designated work area'
            }
          }
        }
      }
    } catch (error) {
      console.warn('Could not check location:', error)
      // If location check fails, we don't mark it as an issue
      // as the error might be due to permissions or other reasons
    }

    return {
      hasIssues,
      notClockedIn,
      notClockedOut,
      outOfLocation,
      issues,
      messages,
    }
  }

  // ==================== TIME ADJUSTMENTS ====================

  // Load time adjustments for company
  const loadTimeAdjustments = async () => {
    if (!authStore.userProfile?.companyId) {
      throw new Error('Company ID not found')
    }

    loading.value = true
    try {
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      const adjustmentsQuery = businessOwnerSubCompanyId
        ? query(
            collection(db, 'timeAdjustments'),
            where('companyId', '==', authStore.userProfile.companyId),
            where('subCompanyId', '==', businessOwnerSubCompanyId),
            orderBy('adjustmentTime', 'desc'),
            limit(100),
          )
        : query(
            collection(db, 'timeAdjustments'),
            where('companyId', '==', authStore.userProfile.companyId),
            orderBy('adjustmentTime', 'desc'),
            limit(100),
          )

      const snapshot = await getDocs(adjustmentsQuery)
      return snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          originalTime: data.originalTime.toDate(),
          adjustedTime: data.adjustedTime.toDate(),
          adjustmentTime: data.adjustmentTime.toDate(),
          createdAt: data.createdAt?.toDate(),
        }
      })
    } catch (error) {
      console.error('Error loading time adjustments:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const loadStaffActivityLogs = async ({ limitCount = 300 } = {}) => {
    if (!authStore.userProfile?.companyId) {
      throw new Error('Company ID not found')
    }
    if (!authStore.isManager) {
      throw new Error('Only managers can view staff activity logs')
    }

    loading.value = true
    try {
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      const logsQuery = businessOwnerSubCompanyId
        ? query(
            collection(db, 'staffActivityLogs'),
            where('companyId', '==', authStore.userProfile.companyId),
            where('subCompanyId', '==', businessOwnerSubCompanyId),
            orderBy('createdAt', 'desc'),
            limit(limitCount),
          )
        : query(
            collection(db, 'staffActivityLogs'),
            where('companyId', '==', authStore.userProfile.companyId),
            orderBy('createdAt', 'desc'),
            limit(limitCount),
          )

      const snapshot = await getDocs(logsQuery)
      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        const createdAt = data.createdAt?.toDate
          ? data.createdAt.toDate()
          : data.createdAt
            ? new Date(data.createdAt)
            : null
        return {
          id: docSnap.id,
          ...data,
          createdAt,
        }
      })
    } catch (error) {
      console.error('Error loading staff activity logs:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Create time adjustment and update attendance record
  const createTimeAdjustment = async (adjustmentData) => {
    if (!authStore.userProfile?.companyId) {
      throw new Error('Company ID not found')
    }

    if (!authStore.isManager) {
      throw new Error('Only managers can create time adjustments')
    }

    loading.value = true
    try {
      const { employeeId, recordType, originalTime, adjustedTime, reason, attendanceRecordId } =
        adjustmentData

      // Validate times
      const original = new Date(originalTime)
      const adjusted = new Date(adjustedTime)

      if (original.getTime() === adjusted.getTime()) {
        throw new Error('Adjusted time must be different from original time')
      }

      // Find and update the attendance record if attendanceRecordId is provided
      let videoUrl = null
      if (attendanceRecordId) {
        const attendanceRef = doc(db, 'attendance', attendanceRecordId)
        const attendanceDoc = await getDoc(attendanceRef)

        if (!attendanceDoc.exists()) {
          throw new Error('Attendance record not found')
        }

        const attendanceData = attendanceDoc.data()
        videoUrl = attendanceData.videoUrl || null

        // Verify the record matches
        if (attendanceData.userId !== employeeId) {
          throw new Error('Employee ID mismatch')
        }

        if (attendanceData.type !== recordType) {
          throw new Error('Record type mismatch')
        }

        // Update the attendance record with adjusted time
        await updateDoc(attendanceRef, {
          timestamp: Timestamp.fromDate(adjusted),
          originalTimestamp: attendanceData.timestamp, // Keep original for audit
          isAdjusted: true,
          adjustedBy: authStore.user.uid,
          adjustedAt: Timestamp.fromDate(new Date()),
          updatedAt: Timestamp.fromDate(new Date()),
        })
      }

      // Create time adjustment record for audit trail
      const adjustmentRecord = applySubCompanyMetadata({
        employeeId,
        companyId: authStore.userProfile.companyId,
        recordType,
        originalTime: original,
        adjustedTime: adjusted,
        reason: reason.trim(),
        adjustedBy: authStore.user.uid,
        adjustmentTime: new Date(),
        attendanceRecordId: attendanceRecordId || null,
        videoUrl,
        createdAt: new Date(),
      }, authStore.userProfile)

      const adjustmentRef = await addDoc(collection(db, 'timeAdjustments'), {
        ...adjustmentRecord,
        originalTime: Timestamp.fromDate(adjustmentRecord.originalTime),
        adjustedTime: Timestamp.fromDate(adjustmentRecord.adjustedTime),
        adjustmentTime: Timestamp.fromDate(adjustmentRecord.adjustmentTime),
        createdAt: Timestamp.fromDate(adjustmentRecord.createdAt),
      })

      // Refresh attendance records
      await loadAllCompanyAttendanceRecords()

      return {
        id: adjustmentRef.id,
        ...adjustmentRecord,
      }
    } catch (error) {
      console.error('Error creating time adjustment:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Find attendance record by employee, type, and approximate time
  const findAttendanceRecord = async (employeeId, recordType, approximateTime) => {
    try {
      const targetTime = new Date(approximateTime)
      const startTime = new Date(targetTime)
      startTime.setHours(targetTime.getHours() - 2) // 2 hours before
      const endTime = new Date(targetTime)
      endTime.setHours(targetTime.getHours() + 2) // 2 hours after

      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      const attendanceQuery = businessOwnerSubCompanyId
        ? query(
            collection(db, 'attendance'),
            where('userId', '==', employeeId),
            where('companyId', '==', authStore.userProfile.companyId),
            where('subCompanyId', '==', businessOwnerSubCompanyId),
            where('type', '==', recordType),
            where('timestamp', '>=', Timestamp.fromDate(startTime)),
            where('timestamp', '<=', Timestamp.fromDate(endTime)),
            orderBy('timestamp', 'desc'),
            limit(1),
          )
        : query(
            collection(db, 'attendance'),
            where('userId', '==', employeeId),
            where('companyId', '==', authStore.userProfile.companyId),
            where('type', '==', recordType),
            where('timestamp', '>=', Timestamp.fromDate(startTime)),
            where('timestamp', '<=', Timestamp.fromDate(endTime)),
            orderBy('timestamp', 'desc'),
            limit(1),
          )

      const snapshot = await getDocs(attendanceQuery)
      if (snapshot.empty) {
        return null
      }

      const doc = snapshot.docs[0]
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        timestamp: data.timestamp.toDate(),
      }
    } catch (error) {
      console.error('Error finding attendance record:', error)
      return null
    }
  }

  // Create manual attendance record (manager only)
  const createManualAttendance = async (data) => {
    if (!authStore.isManager) {
      throw new Error('Only managers can create manual attendance')
    }

    try {
      const timestamp = new Date(data.timestamp)
      const attendanceRecord = {
        userId: data.employeeId,
        companyId: authStore.userProfile.companyId,
        subCompanyId: getEmployeeScopedSubCompanyId(data.employeeId),
        type: data.type,
        timestamp: Timestamp.fromDate(timestamp),
        location: data.location || null,
        faceMatchConfidence: 100,
        videoUrl: null,
        isManual: true,
        isAdjusted: true,
        adjustedBy: authStore.user.uid,
        reason: data.reason || 'Manual Entry',
        branchId: data.branchId || null,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      }

      const docRef = await addDoc(collection(db, 'attendance'), attendanceRecord)
      await loadAllCompanyAttendanceRecords()
      return docRef.id
    } catch (error) {
      console.error('Error creating manual attendance:', error)
      throw error
    }
  }

  // Force clock out an employee (manager only)
  const forceClockOut = async (employeeId, reason = 'Force Clock Out by Manager') => {
    if (!authStore.isManager) {
      throw new Error('Only managers can force clock out employees')
    }

    try {
      // Find the latest record for this employee to get shift/branch details
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      const latestRecordQuery = businessOwnerSubCompanyId
        ? query(
            collection(db, 'attendance'),
            where('userId', '==', employeeId),
            where('companyId', '==', authStore.userProfile.companyId),
            where('subCompanyId', '==', businessOwnerSubCompanyId),
            orderBy('timestamp', 'desc'),
            limit(1)
          )
        : query(
            collection(db, 'attendance'),
            where('userId', '==', employeeId),
            where('companyId', '==', authStore.userProfile.companyId),
            orderBy('timestamp', 'desc'),
            limit(1)
          )
      
      const snapshot = await getDocs(latestRecordQuery)
      let branchId = null
      let shiftId = null
      
      if (!snapshot.empty) {
        const lastRecord = snapshot.docs[0].data()
        branchId = lastRecord.branchId || null
        shiftId = lastRecord.shiftId || null
      }

      const attendanceRecord = {
        userId: employeeId,
        companyId: authStore.userProfile.companyId,
        subCompanyId: getEmployeeScopedSubCompanyId(employeeId),
        type: 'clock-out',
        timestamp: Timestamp.fromDate(new Date()),
        location: null,
        faceMatchConfidence: 100,
        videoUrl: null,
        isManual: true,
        isAdjusted: true,
        adjustedBy: authStore.user.uid,
        reason: reason,
        branchId,
        shiftId,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      }

      const docRef = await addDoc(collection(db, 'attendance'), attendanceRecord)
      await loadAllCompanyAttendanceRecords()
      return docRef.id
    } catch (error) {
      console.error('Error forcing clock out:', error)
      throw error
    }
  }

  // Delete attendance record (manager only)
  const deleteAttendanceRecord = async (recordId) => {
    if (!authStore.isManager) {
      throw new Error('Only managers can delete attendance records')
    }

    try {
      await deleteDoc(doc(db, 'attendance', recordId))
      
      // Update local state
      attendanceRecords.value = attendanceRecords.value.filter(r => r.id !== recordId)
      
      // If we are in the middle of a session, we might need to refresh other views
      await loadAllCompanyAttendanceRecords()
      return true
    } catch (error) {
      console.error('Error deleting attendance record:', error)
      throw error
    }
  }

  // Load system-wide configurations
  const loadSystemConfigs = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'system_configs', 'attendance'))
      if (configDoc.exists()) {
        systemConfigs.value.attendance = {
          ...systemConfigs.value.attendance,
          ...configDoc.data()
        }
      }
    } catch (error) {
      console.error('Error loading system configs:', error)
    }
  }

  // Get attendance records for a specific employee and type within a date range
  const getEmployeeRecordsByType = async (employeeId, recordType, days = 30) => {
    try {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(endDate.getDate() - days)

      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      const attendanceQuery = businessOwnerSubCompanyId
        ? query(
            collection(db, 'attendance'),
            where('userId', '==', employeeId),
            where('companyId', '==', authStore.userProfile.companyId),
            where('subCompanyId', '==', businessOwnerSubCompanyId),
            where('type', '==', recordType),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate)),
            orderBy('timestamp', 'desc')
          )
        : query(
            collection(db, 'attendance'),
            where('userId', '==', employeeId),
            where('companyId', '==', authStore.userProfile.companyId),
            where('type', '==', recordType),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate)),
            orderBy('timestamp', 'desc')
          )

      const snapshot = await getDocs(attendanceQuery)
      return snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp.toDate()
        }
      })
    } catch (error) {
      console.error('Error fetching employee records by type:', error)
      throw error
    }
  }

  // Get currently clocked-in sessions (last punch is clock-in, across days until clock-out)
  const currentlyClockedInSessions = computed(() => {
    const employeeLastRecords = {}

    attendanceRecords.value.forEach((record) => {
      if (!record?.userId) return
      const recordDate = record.timestamp?.toDate
        ? record.timestamp.toDate()
        : new Date(record.timestamp)
      if (Number.isNaN(recordDate.getTime())) return

      const prev = employeeLastRecords[record.userId]
      if (!prev) {
        employeeLastRecords[record.userId] = {
          type: record.type,
          date: recordDate,
          recordId: record.id,
        }
        return
      }

      const prevTime = prev.date.getTime()
      const nextTime = recordDate.getTime()
      // Prefer later timestamps; on ties prefer clock-out so a paired out isn't missed
      if (
        nextTime > prevTime ||
        (nextTime === prevTime && record.type === 'clock-out' && prev.type === 'clock-in')
      ) {
        employeeLastRecords[record.userId] = {
          type: record.type,
          date: recordDate,
          recordId: record.id,
        }
      }
    })

    return Object.entries(employeeLastRecords)
      .filter(([, last]) => last.type === 'clock-in')
      .map(([userId, last]) => ({
        userId,
        clockInAt: last.date,
        recordId: last.recordId,
      }))
      .sort((a, b) => b.clockInAt - a.clockInAt)
  })

  const currentlyClockedInIds = computed(() =>
    currentlyClockedInSessions.value.map((session) => session.userId),
  )

  return {
    attendanceRecords,
    geofences,
    loading,
    clockActionInFlight,
    deliveryMessage,
    activeRosterWindow,
    lastSuccessfulPunchAt,
    isClockActionOnCooldown,
    getClockActionCooldownRemaining,
    CLOCK_ACTION_COOLDOWN_MS,
    currentLocation,
    systemConfigs,
    isFaceRecognitionGloballyEnabled,
    todaysRecords,
    isClockedIn,
    activeClockInRecord,
    currentGeofence,
    currentSchedule,
    lastReminderDismissed,
    lastClockInReminderDismissed,
    getCurrentLocation,
    clockInOut,
    loadTodaysAttendance,
    loadAttendanceHistory,
    loadCompanyAttendanceRecords,
    loadAllCompanyAttendanceRecords,
    loadGeofences,
    createGeofence,
    detectAttendanceIssues,
    loadTimeAdjustments,
    loadStaffActivityLogs,
    createTimeAdjustment,
    createManualAttendance,
    deleteAttendanceRecord,
    findAttendanceRecord,
    getEmployeeRecordsByType,
    checkClockOutReminder,
    dismissReminder,
    checkClockInReminder,
    dismissClockInReminder,
    loadSystemConfigs,
    forceClockOut,
    currentlyClockedInIds,
    currentlyClockedInSessions,
    loadCurrentSchedule
  }
})
